import { defineStore } from 'pinia';
import axiosInstance from '@/services/axios.js';

export const useUserStore = defineStore('user', {
  state: () => ({
    id: '',
    username: localStorage.getItem('username') || '',
    email: '',
    emailVerifiedAt: null,
    balance: { 
      views: 0, 
      clicks: 0, 
      total: 0 
    },
    userRole: localStorage.getItem('userrole') || '',
    lastIpAddr: null,
    fullUserName: null,
    companyName: null,
    companyRole: null,
    userStatus: null,
    createdAt: '',
    updatedAt: '',
    // Дополнительные поля для новых ролей
    profileData: {
      specialization: null,
      experience: null,
      rating: null,
      vehicles: [],
      insuranceType: null,
      languages: [],
      certifications: []
    }
  }),

  actions: {
    // Action to set user data
    setUserData(userData) {
      this.id = userData.id;
      this.username = userData.username || userData.name || userData.email;
      this.email = userData.email;
      this.emailVerifiedAt = userData.email_verified_at;
      this.balance = userData.balance || { views: 0, clicks: 0, total: 0 };
      this.userRole = userData.userrole;
      this.lastIpAddr = userData.lastipaddr;
      this.fullUserName = userData.fullusername;
      this.companyName = userData.companyname;
      this.companyRole = userData.companyrole;
      this.createdAt = userData.created_at;
      this.userStatus = userData.status;
      this.updatedAt = userData.updated_at;
      
      // Устанавливаем дополнительные данные профиля
      if (userData.profile_data) {
        this.profileData = {
          specialization: userData.profile_data.specialization || null,
          experience: userData.profile_data.experience || null,
          rating: userData.profile_data.rating || null,
          vehicles: userData.profile_data.vehicles || [],
          insuranceType: userData.profile_data.insurance_type || null,
          languages: userData.profile_data.languages || [],
          certifications: userData.profile_data.certifications || []
        };
      }
    },

    // Action to clear user data
    clearUserData() {
      this.id = '';
      this.username = '';
      this.email = '';
      this.emailVerifiedAt = null;
      this.userRole = '';
      this.balance = { views: 0, clicks: 0, total: 0 };
      this.lastIpAddr = null;
      this.fullUserName = null;
      this.companyName = null;
      this.companyRole = null;
      this.createdAt = '';
      this.updatedAt = '';
      this.userStatus = null;
      this.profileData = {
        specialization: null,
        experience: null,
        rating: null,
        vehicles: [],
        insuranceType: null,
        languages: [],
        certifications: []
      };
      localStorage.removeItem('username');
      localStorage.removeItem('userrole');
      localStorage.removeItem('token');
    },

    // Fetch additional profile data based on role
    async fetchProfileData() {
      try {
        const endpoint = this.getProfileEndpoint();
        if (endpoint) {
          const response = await axiosInstance.get(endpoint);
          if (response.data) {
            this.profileData = {
              specialization: response.data.specialization || null,
              experience: response.data.experience || null,
              rating: response.data.rating || null,
              vehicles: response.data.vehicles || [],
              insuranceType: response.data.insurance_type || null,
              languages: response.data.languages || [],
              certifications: response.data.certifications || []
            };
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    },

    // Helper to get profile endpoint based on role
    getProfileEndpoint() {
      switch (this.userRole) {
        case 'guide':
          return '/guide/profile';
        case 'transporter':
          return '/transporter/profile';
        case 'insurer':
          return '/insurer/profile';
        case 'client':
          return '/client/profile';
        default:
          return null;
      }
    }
  },

  getters: {
    // Role-based getters
    isAdmin: (state) => state.userRole === 'admin',
    isGuide: (state) => state.userRole === 'guide',
    isClient: (state) => state.userRole === 'client',
    isInsurer: (state) => state.userRole === 'insurer',
    isTransporter: (state) => state.userRole === 'transporter',
    isManager: (state) => state.userRole === 'manager',
    isModerator: (state) => state.userRole === 'moderator',
    
    // Permission checkers
    canManageUsers: (state) => ['admin', 'moderator'].includes(state.userRole),
    canManageBookings: (state) => ['admin', 'manager', 'guide', 'transporter'].includes(state.userRole),
    canViewReports: (state) => ['admin', 'manager', 'insurer'].includes(state.userRole),
    canCreateTours: (state) => ['admin', 'guide', 'manager'].includes(state.userRole),
    canManageVehicles: (state) => ['admin', 'transporter', 'manager'].includes(state.userRole),
    canHandleInsurance: (state) => ['admin', 'insurer', 'manager'].includes(state.userRole),

    // Role name in Russian
    roleName: (state) => {
      switch (state.userRole) {
        case 'admin':
          return 'Администратор';
        case 'moderator':
          return 'Модератор';
        case 'client':
          return 'Клиент';
        case 'guide':
          return 'Гид';
        case 'insurer':
          return 'Страховщик';
        case 'transporter':
          return 'Транспортник';
        case 'manager':
          return 'Менеджер';
        case 'tester':
          return 'Тестировщик';
        case 'support':
          return 'Тех. поддержка';
        case 'partner':
          return 'Партнёр';
        case 'agent':
          return 'Агент';
        case 'monitor':
          return 'Монитор';
        default:
          return 'Пользователь';
      }
    },

    // Role icon/color helpers
    roleColor: (state) => {
      switch (state.userRole) {
        case 'admin':
          return 'error'; // red
        case 'moderator':
          return 'warning'; // orange
        case 'guide':
          return 'success'; // green
        case 'client':
          return 'primary'; // blue
        case 'insurer':
          return 'info'; // teal
        case 'transporter':
          return 'indigo'; // indigo
        case 'manager':
          return 'purple'; // purple
        default:
          return 'default';
      }
    },

    roleIcon: (state) => {
      switch (state.userRole) {
        case 'admin':
          return 'mdi-shield-crown';
        case 'moderator':
          return 'mdi-shield-account';
        case 'guide':
          return 'mdi-map-marker';
        case 'client':
          return 'mdi-account';
        case 'insurer':
          return 'mdi-shield-check';
        case 'transporter':
          return 'mdi-truck';
        case 'manager':
          return 'mdi-briefcase';
        default:
          return 'mdi-account';
      }
    },

    // Check if user has complete profile (role-specific)
    hasCompleteProfile: (state) => {
      const requiredFields = {
        admin: ['fullUserName', 'emailVerifiedAt'],
        guide: ['fullUserName', 'profileData.specialization', 'profileData.languages'],
        client: ['fullUserName', 'emailVerifiedAt'],
        insurer: ['fullUserName', 'companyName', 'profileData.insuranceType'],
        transporter: ['fullUserName', 'companyName', 'profileData.vehicles'],
        manager: ['fullUserName', 'companyName'],
      };

      const fields = requiredFields[state.userRole] || requiredFields.admin;
      
      return fields.every(field => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], state);
        return value && (Array.isArray(value) ? value.length > 0 : true);
      });
    },

    // Check if user is verified
    isVerified: (state) => {
      return state.emailVerifiedAt !== null && state.userStatus === 'active';
    }
  }
});